import moment from "moment";

class OrderUtil {
    getOnlinePaymentProvider(payments){
        if(typeof(payments) !== "undefined" && payments !== null && payments.length > 0){
            for (let i = 0; i < payments.length; i++) {
                let payment = payments[i];
                return payment.onlinePaymentProvider;
            }
        } else {
            return 'NOT_ANY';
        }
    }

    getNameText(name){
        if(name == 'NOT_SET'){
            return 'Ikke oppgitt';
        } else {
            return name;
        }
    }

    getPhoneNrText(mobileNumber){
        if(mobileNumber == 'NOT_SET'){
            return 'Ikke oppgitt';
        } else {
            return mobileNumber;
        }
    }

    getOrderStatusText(orderStatus){
        if(orderStatus == 'ORDERED'){
            return 'Bestilt';
        } else if(orderStatus == 'CONFIRMED'){
            return 'Bekreftet';
        } else if(orderStatus == 'COMPLETED'){
            return 'Fullført';
        } else if(orderStatus == 'CANCELLED') {
            return "Kansellert";
        } else if(orderStatus == 'DRAFT') {
            // Still open on the till: a table whose guests paid parts in «Hver for seg» while the rest is neither sent
            // nor paid. Shown as «NOT_SET» until 2026-10-28.
            return "Åpen";
        } else {
            return "NOT_SET";
        }
    }

    getPaymentStatus(payments){
        if(payments !== undefined){
            if(payments.length > 0){
                return 'PAID';
            } else {
                return 'NOT_PAID_YET';
            }
        }else{
            return 'NOT_PAID_YET';
        }
    }

    getPaymentStatusText(payments){
        if(typeof(payments) !== "undefined" && payments !== null && payments.length > 0){
            if(payments.length > 1){
                const allNonCash = payments.every(payment => payment.paymentType !== 'CASH'); // Check if all payments have paymentType != CASH
                if(allNonCash){
                    return 'Betalt(HVER FOR SEG)';
                } else {
                    return 'Betalt(KONTANT & KORT)';
                }
            } else {
                if(payments.length == 1){
                    if(payments[0].paymentType === 'CASH'){
                        return 'Betalt(KONTANT)';
                    } else if(payments[0].paymentType.toUpperCase().includes('VIPPS')){
                        return 'Betalt(VIPPS)';
                    } else if(payments[0].paymentType === 'LOYALTY_POINTS'){
                        return 'Betalt(BONUS)';
                    }else {
                        return 'Betalt(KORT)';
                    }
                }
            }

            return 'Betalt';
        } else {
            return 'Ikke betalt';
        }
    }

    getRefundStatusText(refundedCartItems, cartItems){
        if(typeof(refundedCartItems) !== "undefined" && refundedCartItems !== null && refundedCartItems.length > 0){
            const totalCartItemCount = this.getTotalCartItemCount(cartItems);
            if(refundedCartItems.length >= totalCartItemCount){
                return 'Refundert';
            } else {
                return 'Delvis refundert';
            }
        }
        return '';
    }

    getTotalCartItemCount(cartItems){
        if(typeof(cartItems) === "undefined" || cartItems === null) return 0;
        let count = 0;
        for(let i = 0; i < cartItems.length; i++){
            count += cartItems[i].count;
        }
        return count;
    }

    /**
     * The card brand behind an order's payment type, or "" when it does not name one.
     *
     * <p>Matched by CONTENT, not by equality and not by prefix. The field arrives in three shapes that
     * whole-string matching cannot tell apart: a bare brand from a terminal ("Visa", "BANKAXEPT"), a code
     * the system wrote itself ("CREDIT_CARD"), and a PSP product identifier ("payex.creditcard",
     * "Dintero_psp.creditcard").
     *
     * <p>The guard used to be a prefix allowlist holding only 'payex.', so Dintero's codes sailed straight
     * past it and the orders list showed «Betalt – Dintero_psp.creditcard» to the merchant. An allowlist is
     * the wrong shape here: it has to be extended for every provider, and the failure is silent and
     * customer-facing — the raw identifier simply appears where a brand should be.
     *
     * <p>DISPLAYED the way the brand writes itself. Providers disagree about casing for the same card —
     * Softpay reports "bankaxept" and "BANKAXEPT", Viva "Visa" — so the lookup is case-insensitive, or the
     * same card appears twice in one list. And naive title-casing gets several wrong: "BANKAXEPT" would
     * become "Bankaxept", "JCB" would become "Jcb". A card's own spelling is not a formatting rule.
     */
    cardBrandOf(paymentType){
        if(typeof paymentType !== 'string'){
            return '';
        }
        const raw = paymentType.trim();
        if(raw === ''){
            return '';
        }
        const lower = raw.toLowerCase();

        const canonical = {
            'american express': 'American Express',
            'visa electron': 'Visa Electron',
            'diners club': 'Diners Club',
            'mastercard': 'Mastercard',
            'bankaxept': 'BankAxept',
            'unionpay': 'UnionPay',
            'discover': 'Discover',
            'electron': 'Visa Electron',
            'dankort': 'Dankort',
            'maestro': 'Maestro',
            'diners': 'Diners Club',
            'v pay': 'V Pay',
            'vpay': 'V Pay',
            'amex': 'Amex',
            'visa': 'Visa',
            'jcb': 'JCB'
        };

        // LONGEST NAME FIRST, and the ordering is load-bearing twice: "visa electron" must not be read as
        // "visa", and — the one that bites — "mastercard" CONTAINS "card", so every brand has to be
        // settled before anything generic is considered.
        const names = Object.keys(canonical).sort((a, b) => b.length - a.length);
        for(const name of names){
            if(lower.includes(name)){
                return canonical[name];
            }
        }

        // Everything that means "a card, but we were not told which". Reached only once every brand has
        // been ruled out, which is what makes a bare "card" safe to match by content here.
        const notABrand = ['creditcard', 'credit_card', 'debitcard', 'debit_card', 'bankcard',
                           'paymentcard', 'credit', 'debit', 'card', 'kort', 'unknown', 'other',
                           'none', 'cash', 'mobile_app', 'loyalty_points'];
        if(notABrand.some(word => lower.includes(word))){
            return '';
        }

        // A PSP product code carrying no brand we know — vendor.product, and no card brand contains a dot.
        // Without this, an unrecognised code (instabank.finance) would be title-cased and printed as
        // though it were the name of a card.
        if(raw.includes('.')){
            return '';
        }

        // A one-word brand nobody has catalogued yet: title case is right for the ordinary case and the
        // best guess for the rest.
        return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
    }

    /**
     * Which colour an order's payment badge takes — ONE rule for the orders list and the order detail, which
     * used to disagree: the list dimmed a paid, completed order while the detail kept it green.
     *
     * 'unpaid' (red) for «Ikke betalt»; otherwise 'completed' (the neutral surface, like the COMPLETED status
     * badge — done, out of focus) once the order is COMPLETED, and 'paid' (green) while it is still in play.
     *
     * @param paymentText the payment text shown — getPaymentDisplay(...).text or getPaymentStatusText(...)
     */
    getPaymentBadgeKind(paymentText, orderStatus){
        if(paymentText === 'Ikke betalt'){
            return 'unpaid';
        }
        return orderStatus === 'COMPLETED' ? 'completed' : 'paid';
    }

    /**
     * What to SHOW for an order's payment: an icon for the method, text for the detail.
     *
     * Split from {@link getPaymentStatusText}, which stays a plain string — the orders table still uses it
     * to decide the badge colour, and a filter elsewhere compares status codes rather than this text.
     *
     * The icon is returned as a FULL Font Awesome identity — ['fab', 'cc-visa'] — not a bare name. The
     * prefix differs per card: brand marks are `fab`, everything else is `fas`, and a template that had to
     * know which is which would get it wrong the first time a new brand appeared.
     */
    getPaymentDisplay(payments){
        if(typeof(payments) === "undefined" || payments === null || payments.length === 0){
            return { icon: ['fas', 'exclamation-circle'], text: 'Ikke betalt' };
        }
        if(payments.length > 1){
            const allNonCash = payments.every(payment => payment.paymentType !== 'CASH');
            return allNonCash
                ? { icon: ['fas', 'user-friends'], text: 'Betalt – hver for seg' }
                : { icon: ['fas', 'money-bill-wave'], text: 'Betalt – kontant og kort' };
        }
        const type = payments[0].paymentType || '';
        if(type === 'CASH'){
            return { icon: ['fas', 'money-bill-wave'], text: 'Betalt – kontant' };
        }
        if(type.toUpperCase().includes('VIPPS') || type === 'MOBILE_APP'){
            return { icon: ['fas', 'mobile-alt'], text: 'Betalt – Vipps' };
        }
        if(type === 'LOYALTY_POINTS'){
            return { icon: ['fas', 'star'], text: 'Betalt – bonus' };
        }
        // Matched by NAME, not by the vendor prefix. Dintero, Swedbank Pay and Viva each write their own
        // (payex.applepay, dintero_psp.applepay, …), and pinning one prefix made an Apple Pay order paid
        // through anybody but Swedbank Pay fall through to a plain card.
        const lower = type.toLowerCase();
        if(lower.includes('applepay') || lower.includes('apple_pay')){
            return { icon: ['fab', 'cc-apple-pay'], text: 'Betalt – Apple Pay' };
        }
        if(lower.includes('googlepay') || lower.includes('google_pay')){
            return { icon: ['fab', 'google-pay'], text: 'Betalt – Google Pay' };
        }
        const brand = this.cardBrandOf(type);
        return { icon: this.cardBrandIcon(brand), text: brand ? ('Betalt – ' + brand) : 'Betalt – kort' };
    }

    /**
     * ONE payment as the order's list of payments shows it: the icon and the method alone, «Kontant», «Visa»,
     * «Apple Pay» — the badge's words without its «Betalt –».
     */
    getPaymentLineDisplay(payment){
        const display = this.getPaymentDisplay([payment]);
        const text = display.text.replace(/^Betalt – /, '');
        return { icon: display.icon, text: text.charAt(0).toUpperCase() + text.slice(1) };
    }

    /** How a delregning was paid, as the till records it (paymentMethod). */
    getPaymentMethodText(paymentMethod){
        if(paymentMethod === 'CASH'){
            return 'Kontant';
        } else if(paymentMethod === 'CASH_AND_CARD'){
            return 'Kontant og kort';
        }
        return 'Kort';
    }

    /**
     * The mark for a card brand, or the generic card when there is none.
     *
     * Font Awesome carries eight of these and no more — Maestro, Dankort, UnionPay and Vpay simply have no
     * logo in the set. Those fall back to the solid credit-card, which is correct rather than a gap: a card
     * nobody drew still has to render, and the brand is written beside the icon anyway.
     */
    cardBrandIcon(brand){
        const marks = {
            'visa': 'cc-visa',
            'mastercard': 'cc-mastercard',
            'amex': 'cc-amex',
            'american express': 'cc-amex',
            'diners': 'cc-diners-club',
            'diners club': 'cc-diners-club',
            'visa electron': 'cc-visa',
            'discover': 'cc-discover',
            'jcb': 'cc-jcb'
        };
        const mark = marks[(brand || '').toLowerCase()];
        return mark ? ['fab', mark] : ['fas', 'credit-card'];
    }

    getPaymentTypeText(paymentType){
        if(paymentType == 'CASH'){
            return 'Kontant';
        } else if(paymentType == 'MOBILE_APP'){
            return 'Vipps';
        } else if(paymentType.includes('vipps')){
            return 'Vipps';
        } else if(paymentType.includes('creditcard')){
            return 'Kort';
        } else if(paymentType == 'payex.applepay'){
            return 'Apple Pay';
        } else if(paymentType == 'payex.googlepay'){
            return 'Google Pay';
        } else if(paymentType == 'DEBIT_CARD' || paymentType == 'CREDIT_CARD'){
            return 'Kort';
        } else if(paymentType == 'LOYALTY_POINTS'){
            return 'Bonus';
        }else {
            return "Kort";
        }
    }

    getOrderTypeText(orderType){
        if(orderType == 'DINE_IN'){
            return 'Spise her';
        } else if(orderType == 'PICKUP'){
            return 'Ta med';
        } else if(orderType == 'DELIVERY'){
            return 'Levere';
        } else {
            return "NOT_SET";
        }
    }

    getChannelText(order){
        if(order.channel == 'APP_IOS'){
            return 'iPhone';
        } else if(order.channel == 'APP_ANDROID'){
            return 'Android';
        } else if(order.channel == 'WEBSHOP'){
            return 'Nettbutikk';
        } else if(order.channel == 'POS'){
            return 'POS(id:' + order.registeredAtTerminalId + ")";
        } else if(order.channel == 'KIOSK'){
            return 'Selvbetjeningskiosk(id:' + order.registeredAtTerminalId + ')';
        } else {
            return "NOT_SET";
        }
    }

    getDateTimeText(dateTime){
        var dateTimeMoment = moment(dateTime);
        return dateTimeMoment.format('DD.MM.YYYY HH:mm:ss');
    }

    getDateTimeTextShort(dateTime){
        var dateTimeMoment = moment(dateTime);
        return dateTimeMoment.format('DD.MM.YYYY HH:mm');
    }

    /**
     * A share of a line, as a till writes it when guests split a wine bottle or the bill (2026-10-26): a line of one
     * with its own amount (shareAmount, VAT included) and its fraction of the original line.
     */
    isShare(cartItem){
        return !!cartItem && cartItem.shareDenominator > 0;
    }

    shareAmount(cartItem){
        return Math.round((parseFloat(cartItem.shareAmount) || 0) * 100) / 100;
    }

    /** « (1/3)», or « (1/4 av 2 stk)», after the item's name; empty for a whole line. */
    shareSuffix(cartItem){
        if(!this.isShare(cartItem)){
            return '';
        }
        let fraction = (cartItem.shareNumerator || 1) + '/' + cartItem.shareDenominator;
        let ofCount = cartItem.shareOfCount || 1;
        let suffix = ' (' + (ofCount > 1 ? fraction + ' av ' + ofCount + ' stk' : fraction) + ')';
        // A refunded share is stored with it in its name already (the refund pages send the name the return receipt
        // prints): «Banh mi med biff (1/3) (1/3)» otherwise.
        return (cartItem.itemName || '').endsWith(suffix) ? '' : suffix;
    }

    getPriceWithVat(vatRate, price){
        let priceWithVat = parseFloat(price) + ((parseFloat(price) / 100) * vatRate);
        return priceWithVat;
    }

    /** DELETED
    getRefundTotalPricesWithVatInCart(cartItems, orderType, vatDoc) {
        let totalPricesInCart = 0.00;
        for (let i = 0; i < cartItems.length; i++) {
            let cartItem = cartItems[i];
            let vatRate = VatUtil.getVatRate(vatDoc, orderType, cartItem.itemType);
            if(cartItem.discountPercent == 0){
                let cartItemPrice = Math.round(this.getPriceWithVat(vatRate, cartItem.price)) * cartItem.count; //make it exact as it is in order description.
                totalPricesInCart = totalPricesInCart + cartItemPrice;
            } else {
                let cartItemPrice = this.getPriceWithVat(vatRate, cartItem.price) * cartItem.count * (100 - cartItem.discountPercent) / 100;
                totalPricesInCart = totalPricesInCart + cartItemPrice;
            }
        }
        return totalPricesInCart;
    }
     */

}

export default new OrderUtil();
